// Datos del profesional por turno (113 bloque K, 114 bloque M): el servidor solo puede armar UN firmante (el de la sesión). Para el
// turno que firma, el formulario manda esta marca en vez de datos tecleados y `renderizarPdf` la reemplaza SIEMPRE con el nombre, la
// identificación y la fecha del firmante real (`FirmaPdf`) — el navegador no puede poner ahí otro nombre. Los otros turnos se capturan
// como texto estructurado (nombres, apellidos, documento) porque son otras personas que no firman este documento.
import { fechaHoraEc } from './layout';
import type { ParPdf } from './especificacion';

/**
 * Formularios cuyo papel oficial (Fe de Errata AC-00115-2021, bloque "DATOS DEL PROFESIONAL RESPONSABLE") rotula el identificador del
 * profesional como "NÚMERO DE DOCUMENTO DE IDENTIFICACIÓN" (cédula) — el servidor no tiene la cédula, solo la matrícula, así que en
 * estos `tipoDocumento` se teclea en el panel de firma y el cuerpo del PDF imprime "Cédula <n>". Verificado contra el PDF (auditoría
 * 2026-09-23, T6), hoja/bloque entre paréntesis (pág. del visor):
 *   002 K (11), 003 L (16), 006 J (25), 007 G (29-30), 008 P (36), 010A D/C (41-42), 010B D (46), 012A G (52), 012B F (56),
 *   013A I (60), 013B H (67), 016 O (69), 019 I (94), 028A F/K (116-117), 028B M (125), 033 O (161), 038 F (170), 052 L (181),
 *   053 F/H (187), 056 R (193), 058 R (210), 058A C (214-216), 113 K (221), 114 M (instructivo, pág. 232), 121 K (263), y los certificados
 *   117 / 116-cert (que ya la pedían).
 * T6b (2026-09-24): 018 J (pág. 82) también rotula "número de documento de identificación" y se agrega. Ya NO teclean su bloque de
 * profesional: 013B H, 018 J, 019 I, 052 L, 056 R y 058 R (UN solo profesional: lo arma el servidor, como 008 P) y 016 O (INICIAL y
 * EGRESO) y 058A C (una por hoja) con `MARCA_FIRMANTE_SERVIDOR` para quien firma + texto estructurado para los demás (como 113/114).
 * T6c (2026-09-25): 053 F y H (un solo profesional por mitad: quien emite y firma) y la evolución de 038 (CÓDIGO/FIRMA por fila; el bloque F
 * de 038 nunca se tecleaba) tampoco se teclean: los arma el servidor. Lo que 053 le asigna a OTRO establecimiento (bloque B destino, G
 * evaluación) sigue tecleado.
 * NO están (el papel pide otra cosa, se sigue imprimiendo la matrícula del servidor): 018A S y 057 O ("sello y/o código"), 024 C/D/E ("Firma, sello y código", U8), 028C K
 * ("CÓDIGO"; el instructivo dice cédula pero la hoja manda), 060 F, 118 F y 122 D ("firma y sello"/"sello y código"), y los que no
 * traen bloque de profesional (005, 016A/B/C, 020, 022, 057A, 119).
 */
export const CODIGOS_CON_CEDULA_PROFESIONAL: readonly string[] = [
  '002',
  '003',
  '006',
  '007',
  '008',
  '010A',
  '010B',
  '012A',
  '012B',
  '013A',
  '013B',
  '016',
  '018',
  '019',
  '028A',
  '028B',
  '033',
  '038',
  '052',
  '053',
  '056',
  '058',
  '058A',
  '113',
  '114',
  '116-cert',
  '117',
  '121',
];

export const MARCA_FIRMANTE_SERVIDOR = '@@firmante-del-servidor@@';

interface FirmanteServidor {
  readonly practitionerNombre: string;
  readonly practitionerRegistro: string;
  readonly fechaISO: string;
}

export function textoFirmanteServidor(firma: FirmanteServidor, etiquetaIdentificacion?: string): string {
  return `${firma.practitionerNombre} — ${etiquetaIdentificacion ?? 'Registro MSP/ACESS'} ${firma.practitionerRegistro} — ${fechaHoraEc(firma.fechaISO)}`;
}

/** Reemplaza `MARCA_FIRMANTE_SERVIDOR` (respuesta exacta) por el texto del firmante de la sesión. El resto de los pares queda idéntico. */
export function sustituirMarcaFirmante(pares: readonly ParPdf[], firma: FirmanteServidor, etiquetaIdentificacion?: string): ParPdf[] {
  return pares.map((p) => (p.respuesta.trim() === MARCA_FIRMANTE_SERVIDOR ? { ...p, respuesta: textoFirmanteServidor(firma, etiquetaIdentificacion) } : p));
}
